import { useEffect } from 'react';
import { useUIStore } from '../../stores/uiStore';
import type { ToastState } from '../../stores/uiStore';
import { useToast } from '../../hooks/ui/useToast';

/**
 * Forwards `useUIStore().showToast(...)` writes to the rendered toast system (`ToastProvider`).
 *
 * The app has two toast APIs: the Zustand store slot (`uiStore.toast`, called from dozens of
 * files) and the context provider (`useToast`, rendered in App.tsx). Until this bridge existed
 * the store slot had no renderer, so every store toast (sale errors, "Payment recorded",
 * "Credit limit updated", plan-limit messages) was silently dropped. Mount this once, inside
 * `<ToastProvider>`. Renders nothing.
 *
 * Mapping: `type` -> `variant` (identical unions), `message` -> `title`,
 * `duration` -> `durationMs` (falls back to the provider's per-variant default when absent),
 * `id` -> `id` (dedupe key; same id replaces instead of stacking).
 */
export function UiStoreToastBridge(): null {
  const toast = useToast();

  useEffect(() => {
    const flush = (pending: ToastState | null): void => {
      if (!pending) return;
      toast({
        variant: pending.type,
        title: pending.message,
        // Keep absent keys absent rather than `undefined`, so the input also satisfies the
        // stricter `exactOptionalPropertyTypes` shape used by admin-portal, partner-portal
        // and shared-utils (apps/web does not enable that flag today).
        ...(pending.duration !== undefined ? { durationMs: pending.duration } : {}),
        ...(pending.id !== undefined ? { id: pending.id } : {}),
      });
      // Clear AFTER dispatch. The slot is a handoff, not state to render; leaving it set would
      // re-fire the same toast on the next subscribe or remount (StrictMode double-mounts effects).
      useUIStore.getState().clearToast();
    };

    // Drain anything queued before this bridge mounted (a showToast fired during app boot).
    flush(useUIStore.getState().toast);

    // subscribe(), not a selector + effect: the store has ONE slot, so two showToast calls in the
    // same tick would collapse into a single render and the first message would be lost. A
    // subscribe listener runs on every set(). The clearToast() above re-enters this listener with
    // a null toast, which the `if (!pending)` guard ends. Because that nested set() happens inside
    // this listener, any OTHER subscriber receives the (null) notification before this one's outer
    // call returns; harmless today (there is no other subscriber, and React consumers re-read
    // getState()), but a future subscriber should compare against getState(), not its `state` arg.
    return useUIStore.subscribe((state, previous) => {
      if (state.toast !== previous.toast) flush(state.toast);
    });
  }, [toast]);

  return null;
}
